import React, { useState, useEffect, useRef } from 'react';
import ProductCatalogMobile from './ProductCatalogMobile';
import { useNavigate } from 'react-router-dom';

const MobileSecurity = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState({});
  const sectionRef = useRef(null);

  const handleclick = () => {
    navigate('/contact-us');
    window.scrollTo(0, 0);
  };

  const scrollToRef = () => {
    sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('[id^="animate-"]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: "fas fa-mobile-alt",
      title: "Mobile & Tablet Security",
      description: "Advanced security stands with recoiler systems, sensor cables, and adjustable brackets for smartphones and tablets."
    },
    {
      icon: "fas fa-camera",
      title: "Camera Protection",
      description: "Specialized holder stands and lens protection systems designed for camera and photography equipment security."
    },
    {
      icon: "fas fa-headphones",
      title: "Audio Accessories",
      description: "Secure display solutions for headphones, earphones, and audio equipment with charging capabilities."
    }
  ];

  const features = [
    {
      icon: "fas fa-lock",
      title: "High Security",
      description: "Advanced locking mechanisms and steel wire systems ensure maximum protection"
    },
    {
      icon: "fas fa-cog",
      title: "Adjustable Design",
      description: "Flexible brackets and clamps accommodate various device sizes and types"
    },
    {
      icon: "fas fa-bolt",
      title: "Charging Integration",
      description: "Built-in charging capabilities with multiple connector types (Micro, USB-C, Lightning)"
    },
    {
      icon: "fas fa-volume-up",
      title: "Alarm System",
      description: "100dB alarm volume with remote control functionality for enhanced security"
    }
  ];

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="mobile-security-landing">
      {/* Keyframe Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-pulse-custom {
          animation: pulse 2s infinite;
        }
        .delay-0 { animation-delay: 0s; }
        .delay-2 { animation-delay: 2s; }
        .delay-4 { animation-delay: 4s; }
      `}</style>

      {/* Hero Section */}
      <section className="relative min-h-[75vh] bg-gradient-to-br from-[#0f2027] to-[#203a43] overflow-hidden flex items-center font-poppins">
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-30">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
            <defs>
              <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#grid)"/>
          </svg>
        </div>

        {/* Floating Icons */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <i className="fas fa-mobile-alt text-white/10 text-5xl absolute top-[20%] left-[10%] animate-float delay-0"></i>
          <i className="fas fa-tablet-alt text-white/10 text-6xl absolute top-[60%] right-[15%] animate-float delay-2"></i>
          <i className="fas fa-headphones text-white/10 text-5xl absolute bottom-[30%] left-[20%] animate-float delay-4"></i>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="text-white">
              <h1 className="text-4xl lg:text-6xl font-bold mb-8 mt-8">
                Secure Your Electronics
              </h1>
              <p className="text-lg text-white/70 mb-6">
                Professional security solutions for mobile devices, tablets, cameras, and electronic accessories.
                Protect your valuable inventory with our innovative stands and sensor systems.
              </p>
              <button
                onClick={() => scrollToSection('categoriesmobile')}
                className="bg-gradient-to-r from-[#ff6b6b] to-[#ee5a24] text-white font-semibold px-10 py-4 rounded-full shadow-lg shadow-[#ee5a24]/40 hover:shadow-xl hover:shadow-[#ee5a24]/60 hover:-translate-y-0.5 transition-all duration-300"
              >
                Explore Solutions <i className="fas fa-arrow-right ml-2"></i>
              </button>
            </div>
            <div className="text-center">
              <i className="fas fa-shield-alt text-white/30 text-[10rem] animate-pulse-custom"></i>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 bg-slate-50 font-poppins">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">Our Security Solutions</h2>
          <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                id={`animate-service-${index}`}
                className={`bg-white rounded-3xl p-10 text-center shadow-lg transition-all duration-500 h-full ${
                  isVisible[`animate-service-${index}`]
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-8'
                } hover:-translate-y-3 hover:shadow-2xl`}
              >
                <div className="w-20 h-20 bg-gradient-to-br from-[#0f2027] to-[#3ec63e] rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${service.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white font-poppins">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-8">Why Choose Our Solutions?</h2>
              {features.map((feature, index) => (
                <div
                  key={index}
                  id={`animate-feature-${index}`}
                  className={`flex items-center mb-8 transition-all duration-700 ${
                    isVisible[`animate-feature-${index}`]
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 -translate-x-8'
                  } md:flex-row flex-col md:text-left text-center`}
                >
                  <div className="w-15 h-15 bg-gradient-to-br from-[#10b981] to-[#059669] rounded-2xl flex items-center justify-center flex-shrink-0 md:mr-6 mb-4 md:mb-0">
                    <i className={`${feature.icon} text-white text-2xl p-4`}></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">{feature.title}</h4>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center">
              <i className="fas fa-shopping-cart text-slate-100 text-[15rem] mt-12"></i>
            </div>
          </div>
        </div>
      </section>

      {/* Product Catalog Section */}
      <section id="categoriesmobile">
        <ProductCatalogMobile />
      </section>

      {/* CTA Section */}
      <section className="py-16 text-center text-white bg-gradient-to-br from-[#1f2937] to-[#374151] font-poppins">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4">Ready to Secure Your Business?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Contact us today to learn more about our mobile security solutions and how we can protect your valuable inventory.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={handleclick}
              className="bg-white text-[#3ec63e] font-semibold px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors duration-300"
            >
              Request a Demo
            </button>
            <button
              onClick={handleclick}
              className="border-2 border-white text-white font-semibold px-8 py-3 rounded-lg hover:bg-white/10 transition-colors duration-300"
            >
              Contact Sales
            </button>
          </div>
        </div>
      </section>

      {/* Font Awesome CDN */}
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"
      />
    </div>
  );
};

export default MobileSecurity;